import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, USER_ROLES } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';
import PermissionRoute from './PermissionRoute';
import LoginPage from '../pages/auth/LoginPage';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Layouts
import StudentLayout from '../layouts/StudentLayout';
import AdminLayout from '../layouts/AdminLayout';

// Student Pages
import StudentHome from '../pages/student/StudentHome';
import StudentDashboard from '../pages/student/StudentDashboard';
import Soroban from '../pages/student/Soroban';
import FlashAnzan from '../pages/student/FlashAnzan';
import FlashCards from '../pages/student/FlashCards';
import StudentDailyExercises from '../pages/student/DailyExercises';
import Competitions from '../pages/student/Competitions';
import CompetitionDetails from '../pages/student/CompetitionDetails';
import CompetitionRound from '../pages/student/CompetitionRound';
import CompetitionsArchive from '../pages/student/CompetitionsArchive';
import StudentChallenges from '../pages/student/StudentChallenges';
import StudentChallengesArchive from '../pages/student/StudentChallengesArchive';
import StudentChallengeDetail from '../pages/student/StudentChallengeDetail';
import StudentGames from '../pages/student/StudentGames';
import StudentGamePlay from '../pages/student/StudentGamePlay';
import StudentEsorobanNinjaGame from '../pages/student/StudentEsorobanNinjaGame';

// Admin Pages
import AdminDashboard from '../pages/admin/AdminDashboard';
import CompetitionManagement from '../pages/admin/CompetitionManagement';
import ReportsLayout from '../pages/admin/reports/ReportsLayout';
import ReportDailyExercises from '../pages/admin/reports/ReportDailyExercises';
import ReportDailyExercisesReport from '../pages/admin/reports/ReportDailyExercisesReport';
import ReportDailyExercisesStudentSessions from '../pages/admin/reports/ReportDailyExercisesStudentSessions';
import ReportFlashAnzan from '../pages/admin/reports/ReportFlashAnzan';
import ReportFlashAnzanStudentSessions from '../pages/admin/reports/ReportFlashAnzanStudentSessions';
import ReportFlashCards from '../pages/admin/reports/ReportFlashCards';
import ReportFlashCardsStudentSessions from '../pages/admin/reports/ReportFlashCardsStudentSessions';
import ReportGames from '../pages/admin/reports/ReportGames';
import ReportGamesStudentSessions from '../pages/admin/reports/ReportGamesStudentSessions';
import ReportChallenges from '../pages/admin/reports/ReportChallenges';
import ReportChallengesStudentSubmissions from '../pages/admin/reports/ReportChallengesStudentSubmissions';
import ReportUsers from '../pages/admin/reports/ReportUsers';
import Settings from '../pages/admin/Settings';
import Profile from '../pages/admin/Profile';
import EditProfile from '../pages/admin/EditProfile';
import Countries from '../pages/admin/Countries';
import Regions from '../pages/admin/Regions';
import Academies from '../pages/admin/Academies';
import Districts from '../pages/admin/Districts';
import Institutions from '../pages/admin/Institutions';
import Departments from '../pages/admin/Departments';
import LearningLevels from '../pages/admin/LearningLevels';
import Users from '../pages/admin/users/Users';
import UserProfile from '../pages/admin/users/UserProfile';
import Students from '../pages/admin/students/Students';
import StudentProfile from '../pages/admin/students/StudentProfile';
import StudentStatistics from '../pages/admin/students/StudentStatistics';
import CompetitionStudents from '../pages/admin/CompetitionStudents';
import CompetitionParticipants from '../pages/admin/CompetitionParticipants';
import CompetitionRounds from '../pages/admin/CompetitionRounds';
import CompetitionLeaderboard from '../pages/admin/CompetitionLeaderboard';
import DailyExercises from '../pages/admin/DailyExercises';
import Challenges from '../pages/admin/Challenges';
import Games from '../pages/admin/Games';
import FlashAnzanLevelStatistics from '../pages/admin/FlashAnzanLevelStatistics';
import FlashCardsLevelStatistics from '../pages/admin/FlashCardsLevelStatistics';
import PublicCompetitionParticipants from '../pages/public/PublicCompetitionParticipants';
import Leaderboard from '../pages/public/Leaderboard';
import PublicGames from '../pages/public/PublicGames';
import PublicTirALaCordeGame from '../pages/public/PublicTirALaCordeGame';
import { ROUTE_PERMISSIONS } from '../config/permissionsMap';

const AppRoutes = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  const getDefaultRedirect = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'STUDENT') return '/student';
    return '/dashboard';
  };

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? 
            <Navigate to={user?.role === 'STUDENT' ? '/student' : '/dashboard'} replace /> : 
            <LoginPage />
        } 
      />

      <Route 
        path="/" 
        element={<Navigate to={getDefaultRedirect()} replace />}
      />

      {/* طرق الطلاب */}
      <Route path="/student" element={
        <ProtectedRoute studentOnly>
          <StudentLayout />
        </ProtectedRoute>
      }>
        <Route index element={<StudentHome />} />
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="soroban" element={<Soroban />} />
        <Route path="flash-anzan" element={<FlashAnzan />} />
        <Route path="flash-cards" element={<FlashCards />} />
        <Route path="daily-exercises" element={<StudentDailyExercises />} />
        <Route path="competitions" element={<Competitions />} />
        <Route path="competitions/archive" element={<CompetitionsArchive />} />
        <Route path="competitions/:competitionId" element={<CompetitionDetails />} />
        <Route path="competitions/:competitionId/rounds/:roundNumber" element={<CompetitionRound />} />
        <Route path="challenges" element={<StudentChallenges />} />
        <Route path="challenges/archive" element={<StudentChallengesArchive />} />
        <Route path="challenges/:challengeId" element={<StudentChallengeDetail />} />
        <Route path="games" element={<StudentGames />} />
        <Route path="games/esoroban-ninja" element={<StudentEsorobanNinjaGame />} />
        <Route path="games/:gameId" element={<StudentGamePlay />} />
        <Route path="profile" element={<Profile />} />
        <Route path="edit-profile" element={<EditProfile />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* طرق لوحة التحكم الموحدة (admin, super-admin, trainer, supervisor, manager) */}
      <Route path="/dashboard" element={
        <ProtectedRoute adminOnly>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="flash-anzan-level-statistics" element={<FlashAnzanLevelStatistics />} />
        <Route path="flash-cards-level-statistics" element={<FlashCardsLevelStatistics />} />
        <Route path="competitions" element={
          <PermissionRoute>
            <CompetitionManagement />
          </PermissionRoute>
        } />
        <Route path="competitions/:competitionId/students" element={
          <PermissionRoute>
            <CompetitionStudents />
          </PermissionRoute>
        } />
        <Route path="competitions/:competitionId/participants" element={
          <PermissionRoute>
            <CompetitionParticipants />
          </PermissionRoute>
        } />
        <Route path="competitions/:competitionId/rounds" element={
          <PermissionRoute>
            <CompetitionRounds />
          </PermissionRoute>
        } />
        <Route path="competitions/:competitionId/leaderboard" element={
          <PermissionRoute>
            <CompetitionLeaderboard />
          </PermissionRoute>
        } />
        <Route path="reports" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.reports}>
            <ReportsLayout />
          </PermissionRoute>
        }>
          <Route index element={<Navigate to="daily-exercises" replace />} />
          <Route path="daily-exercises-ranking" element={<ReportDailyExercises />} />
          <Route path="daily-exercises/students/:studentId" element={<ReportDailyExercisesStudentSessions />} />
          <Route path="daily-exercises" element={<ReportDailyExercisesReport />} />
          <Route path="flash-anzan/students/:studentId" element={<ReportFlashAnzanStudentSessions />} />
          <Route path="flash-anzan" element={<ReportFlashAnzan />} />
          <Route path="flash-cards/students/:studentId" element={<ReportFlashCardsStudentSessions />} />
          <Route path="flash-cards" element={<ReportFlashCards />} />
          <Route path="games/students/:studentId" element={<ReportGamesStudentSessions />} />
          <Route path="games" element={<ReportGames />} />
          <Route path="challenges/students/:studentId" element={<ReportChallengesStudentSubmissions />} />
          <Route path="challenges" element={<ReportChallenges />} />
          <Route path="users" element={<ReportUsers />} />
        </Route>
        <Route path="countries" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.countries}>
            <Countries />
          </PermissionRoute>
        } />
        <Route path="regions" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.regions}>
            <Regions />
          </PermissionRoute>
        } />
        <Route path="academies" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.academies}>
            <Academies />
          </PermissionRoute>
        } />
        <Route path="districts" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.districts}>
            <Districts />
          </PermissionRoute>
        } />
        <Route path="institutions" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.institutions}>
            <Institutions />
          </PermissionRoute>
        } />
        <Route path="departments" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.departments}>
            <Departments />
          </PermissionRoute>
        } />
        <Route path="learning-levels" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS['learning-levels']}>
            <LearningLevels />
          </PermissionRoute>
        } />
        <Route path="users/:userId" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.users}>
            <UserProfile />
          </PermissionRoute>
        } />
        <Route path="users" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.users}>
            <Users />
          </PermissionRoute>
        } />
        <Route path="students/:studentId" element={
          <PermissionRoute requiredPermissions={ROUTE_PERMISSIONS.students} permissionMode="any">
            <StudentProfile />
          </PermissionRoute>
        } />
        <Route path="students/:studentId/statistics" element={
          <PermissionRoute requiredPermissions={ROUTE_PERMISSIONS.students} permissionMode="any">
            <StudentStatistics />
          </PermissionRoute>
        } />
        <Route path="students" element={
          <PermissionRoute requiredPermissions={ROUTE_PERMISSIONS.students} permissionMode="any">
            <Students />
          </PermissionRoute>
        } />
        <Route path="challenges" element={
          <PermissionRoute allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
            <Challenges />
          </PermissionRoute>
        } />
        <Route path="games" element={
          <PermissionRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
            <Games />
          </PermissionRoute>
        } />
        <Route path="daily-exercises" element={
          <PermissionRoute requiredPermissions={ROUTE_PERMISSIONS['daily-exercises']} permissionMode="any" exercisesVisibility>
            <DailyExercises />
          </PermissionRoute>
        } />
        <Route path="settings" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.settings}>
            <Settings />
          </PermissionRoute>
        } />
        <Route path="profile" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS.profile}>
            <Profile />
          </PermissionRoute>
        } />
        <Route path="edit-profile" element={
          <PermissionRoute requiredPermission={ROUTE_PERMISSIONS['edit-profile']}>
            <EditProfile />
          </PermissionRoute>
        } />
      </Route>

      {/* Public Routes */}
      <Route path="/publicGames" element={<Navigate to="/public/games" replace />} />
      <Route path="/public/games/tir-a-la-corde" element={<PublicTirALaCordeGame />} />
      <Route path="/public/games" element={<PublicGames />} />
      <Route path="/public/competitions/:competitionId/participants" element={<PublicCompetitionParticipants />} />
      <Route path="/leaderboard/:competitionId" element={<Leaderboard />} />

      {/* صفحة 404 */}
      <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
    </Routes>
  );
};

export default AppRoutes;
